import { computed } from 'vue';
import type { ComputedRef } from 'vue';
import { useActionEvents } from '@/composables/useActionEvents';
import { eventHub } from '@/lib/services/eventhub';

// Configurazione del composable
export interface TabEventsConfig {
	entityName: string; // 'order', 'user', 'timecard', etc.

	// Callback opzionali - se non forniti, gli eventi non vengono registrati
	onSave?: () => Promise<void> | void;
	onRefresh?: () => Promise<void> | void;

	// Configurazione per refreshTitle
	titleFormatter?: (entity: Record<string, any>) => string; // Funzione custom per formattare il titolo
	titleProperty?: string; // Default: 'code' - proprietà da usare se titleFormatter non fornito
}

/**
 * Composable per gestione eventi tab centralizzata
 *
 * Gestisce:
 * - Registrazione automatica a onSave/onRefresh se callback fornite
 * - Emissione eventi {entityName}_refreshTitle specifici per entità
 * - Gestione pending save notifications cross-tab ({entityName}_addPendingSave, {entityName}_clearPendingSave)
 * - Esposizione entityName per riuso nel component
 *
 * Elimina il pattern ripetitivo di:
 * - const { onSave, onRefresh } = useActionEvents('entityName')
 * - onSave(saveCallback)
 * - onRefresh(refreshCallback)
 * - eventHub.$emit('order_refreshTitle', entity.code)
 * - eventHub.$emit('order_addPendingSave', 'TabName')
 * - eventHub.$emit('order_clearPendingSave', 'TabName')
 */
export function useTabEvents(config: TabEventsConfig) {
	const {
		entityName,
		onSave: saveCallback,
		onRefresh: refreshCallback,
		titleFormatter,
		titleProperty = 'code',
	} = config;

	// ActionEvents per l'entità specifica
	const { onSave, onRefresh } = useActionEvents(entityName);

	// Auto-registrazione degli eventi se callback fornite
	if (saveCallback) {
		onSave(saveCallback);
	}

	if (refreshCallback) {
		onRefresh(refreshCallback);
	}

	/**
	 * Aggiorna il titolo della tab con il valore formattato
	 * Emette evento specifico per entità: {entityName}_refreshTitle
	 */
	const refreshTitle = (entity: Record<string, any>): void => {
		let titleValue: string;

		if (titleFormatter) {
			// Usa formatter custom se fornito
			titleValue = titleFormatter(entity);
		} else {
			// Altrimenti usa la proprietà specificata
			titleValue = entity[titleProperty];
		}

		if (titleValue) {
			const eventName = `${entityName}_refreshTitle`;
			eventHub.$emit(eventName, titleValue);
		} else {
			console.warn(`⚠️ ${entityName}.refreshTitle: Unable to format title from entity`);
		}
	};

	/**
	 * Aggiunge una tab alla lista delle modifiche pendenti
	 * Emette evento specifico per entità: {entityName}_addPendingSave
	 */
	const addPendingSave = (tabName: string): void => {
		const eventName = `${entityName}_addPendingSave`;
		eventHub.$emit(eventName, tabName);
	};

	/**
	 * Rimuove una tab dalla lista delle modifiche pendenti
	 * Emette evento specifico per entità: {entityName}_clearPendingSave
	 */
	const clearPendingSave = (tabName: string): void => {
		const eventName = `${entityName}_clearPendingSave`;
		eventHub.$emit(eventName, tabName);
	};

	/**
	 * EntityName computed per riuso nel component
	 */
	const entityNameRef: ComputedRef<string> = computed(() => entityName);

	return {
		// Stato
		entityName: entityNameRef,

		// Metodi
		refreshTitle,
		addPendingSave,
		clearPendingSave,

		// Eventi (sempre esposti per registrazione manuale)
		onSave,
		onRefresh,
	};
}
